import React from 'react';
import { Link } from 'react-router-dom';

function Home() {
  const platforms = [
    { name: 'Roku', slug: 'roku' },
    { name: 'Apple TV', slug: 'appletv' },
    { name: 'Android TV', slug: 'android' },
    { name: 'Fire TV', slug: 'firetv', local: true },
    { name: 'Samsung TV', slug: 'samsung' },
    { name: 'LG TV', slug: 'lg' }
  ];

  const features = [
    {
      title: '4K Ultra HD',
      description: 'Crystal-clear playback in stunning 4K resolution with HDR support. Every detail, every frame.',
      icon: '4K',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      title: 'Universal Format Support',
      description: 'Play any video format without conversion. MP4, MKV, AVI, and dozens more out of the box.',
      icon: '{}',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      title: 'Fully Customizable',
      description: 'Dark mode, playback controls, subtitles, picture-in-picture, and personalized settings your way.',
      icon: '\u2699',
      gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)'
    }
  ];

  const steps = [
    { number: '01', title: 'Download', description: 'Get PlayGrid from your device\'s app store' },
    { number: '02', title: 'Activate', description: 'Enter your provider credentials to activate' },
    { number: '03', title: 'Watch', description: 'Start enjoying your favorite content in HD & 4K' }
  ];

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-split">
          <div className="hero-text">
            <img
              src="/logo/playgrid_logo.svg"
              alt="PlayGrid Logo"
              className="hero-logo"
            />
            <h1 className="hero-title">Your Premium Video Player for Every Screen</h1>
            <p className="hero-subtitle">
              Stream in stunning HD & 4K. Watch anywhere, on any device. Professional-grade video playback for your smart TV and mobile devices.
            </p>
            <div className="hero-cta">
              <Link to="/contact" className="btn-primary">Get Started</Link>
            </div>
          </div>
          <div className="hero-phone">
            <div className="phone-frame">
              <div className="phone-notch"></div>
              <div className="phone-screen">
                <div className="phone-ui-header">
                  <span className="phone-ui-brand">PlayGrid</span>
                  <span className="phone-ui-search">&#9906;</span>
                </div>
                <div className="phone-ui-featured">
                  <div className="phone-ui-live-badge">LIVE</div>
                  <div className="phone-ui-featured-text">Now Streaming</div>
                </div>
                <div className="phone-ui-row">
                  <div className="phone-ui-thumb" style={{ '--thumb-delay': '0s', '--thumb-color': '#667eea' }}></div>
                  <div className="phone-ui-thumb" style={{ '--thumb-delay': '0.2s', '--thumb-color': '#f5576c' }}></div>
                  <div className="phone-ui-thumb" style={{ '--thumb-delay': '0.4s', '--thumb-color': '#43e97b' }}></div>
                </div>
                <div className="phone-ui-row">
                  <div className="phone-ui-thumb" style={{ '--thumb-delay': '0.6s', '--thumb-color': '#fa709a' }}></div>
                  <div className="phone-ui-thumb" style={{ '--thumb-delay': '0.8s', '--thumb-color': '#30cfd0' }}></div>
                  <div className="phone-ui-thumb" style={{ '--thumb-delay': '1.0s', '--thumb-color': '#f7931e' }}></div>
                </div>
                <div className="phone-ui-nowplaying">
                  <div className="phone-ui-album"></div>
                  <div className="phone-ui-track">
                    <div className="phone-ui-track-title"></div>
                    <div className="phone-ui-progress">
                      <div className="phone-ui-progress-bar"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-platforms">
          <div className="platforms-row">
            {platforms.map((platform, index) => (
              <div key={index} className="platform-icon-item">
                <img
                  src={platform.local ? `/firetv/icons8-fire-tv-50.png` : `https://cdn.simpleicons.org/${platform.slug}/ffffff`}
                  alt={platform.name}
                  className={`platform-icon-img${platform.local ? ' platform-icon-invert' : ''}`}
                  loading="lazy"
                />
                <span className="platform-icon-name">{platform.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-header">
          <h2 className="section-title">Everything You Need</h2>
          <p className="section-subtitle">
            Powerful features designed for the ultimate viewing experience
          </p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card" style={{ '--card-gradient': feature.gradient }}>
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <h2 className="section-title">How It Works</h2>
        <div className="steps-container">
          {steps.map((step, index) => (
            <div key={index} className="step-card">
              <div className="step-number">{step.number}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-description">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content-simple">
          <div className="footer-brand-center">
            <img src="/logo/playgrid_logo_white.svg" alt="PlayGrid" className="footer-logo" />
            <p className="footer-tagline">Premium video player for every screen</p>
          </div>
          <div className="footer-links-simple">
            <Link to="/contact" className="footer-link">Contact Us</Link>
            <span className="footer-separator">&bull;</span>
            <Link to="/privacy" className="footer-link">Privacy Policy</Link>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="footer-copyright">&copy; 2024 PlayGrid. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Home;
